import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';

// Substitua estas importações pelos componentes reais quando os tiver prontos
import AdminUsuarios from './AdminUsuarios';
import AdminAcessos from './AdminAcessos';
import AdminDados from './AdminDados';
import AdminLogs from './AdminLogs';
import AdminSeguranca from './AdminSeguranca';
import AdminTelegram from './AdminTelegram';
import AdminAutomacoes from './AdminAutomacoes';
import AdminAvisos from './AdminAvisos';

import {
  Users,
  Database,
  Activity,
  ShieldAlert,
  LayoutDashboard,
  ArrowRight,
  Settings,
  Send,
  UploadCloud,
  Bell,
  ShieldCheck
} from 'lucide-react';

const AdminPanel = () => {
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [activeTab, setActiveTab] = useState('dashboard');

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => { return window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'); };

    const modules = [
        { 
            id: 'usuarios', 
            label: 'Usuários', 
            icon: Users, 
            color: '#3b82f6', // Azul
            bgColor: '#eff6ff',
            desc: 'Gerencie acessos, crie contas e edite permissões.',
            stat: 'Gestão de Pessoas'
        },
        {
            id: 'acessos',
            label: 'Acessos',
            icon: ShieldCheck,
            color: '#8b5cf6', // Roxo — mesma cor da ação "Alterar acesso" em Usuários
            bgColor: '#f5f3ff',
            desc: 'Veja quem alcança cada módulo — lido do próprio servidor.',
            stat: 'Controle de Acesso'
        },
        { 
            id: 'dados', 
            label: 'Dados', 
            icon: Database, 
            color: '#10b981', // Verde
            bgColor: '#ecfdf5',
            desc: 'Edite tabelas brutas e registros do sistema.',
            stat: 'Editor SQL'
        },
        {
            id: 'logs',
            label: 'Logs',
            icon: Activity,
            color: '#8b5cf6',
            bgColor: '#f5f3ff',
            desc: 'Histórico de auditoria e rastreio de atividades.',
            stat: 'Auditoria'
        },
        {
            id: 'seguranca',
            label: 'Segurança', 
            icon: ShieldAlert, 
            color: '#e11d48', // Vermelho
            bgColor: '#fff1f2',
            desc: 'Monitoramento de riscos e recuperação de senhas.',
            stat: 'Controle de Acesso'
        },
        {
            id: 'bot-telegram',
            label: 'Bot Telegram',
            icon: Send,
            color: '#0ea5e9',
            bgColor: '#e0f2fe',
            desc: 'Configuração e gerenciamento do robô Telegram.',
            stat: 'Comunicação'
        },
        {
            id: 'automacoes',
            label: 'Automações',
            icon: UploadCloud,
            color: '#f59e0b', // Âmbar
            bgColor: '#fffbeb',
            desc: 'Suba as planilhas de Projeção e Orçamento para o banco.',
            stat: 'Carga de Dados'
        },
        {
            id: 'avisos',
            label: 'Avisos',
            icon: Bell,
            color: '#eab308', // Amarelo (cor do Sino)
            bgColor: '#fefce8',
            desc: 'Publique avisos no Sino da Home, com agendamento e público-alvo.',
            stat: 'Comunicação'
        },
    ];

    // --- VISÃO GERAL (HOME) ---
    const OverviewWidgets = () => (
        <div>
            {/* Card de Boas Vindas */}
            <div style={{ backgroundColor: '#0f172a', borderRadius: '16px', padding: '40px', color: '#fff', marginBottom: '32px', position: 'relative', overflow: 'hidden', boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.3)' }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0 }}>Bem-vindo, Administrador</h2>
                    <p style={{ color: '#94a3b8', fontSize: '18px', maxWidth: '600px', margin: 0, lineHeight: '1.6' }}>
                        Selecione um módulo abaixo para iniciar as suas atividades de gestão e configuração da plataforma Grupo Correia.
                    </p>
                </div>
                {/* Elemento Decorativo */}
                <div style={{ position: 'absolute', right: '-50px', top: '-50px', opacity: 0.1 }}>
                    <Settings size={250} />
                </div>
            </div>

            {/* Grid de Módulos */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
                {modules.map((mod) => (
                    <div 
                        key={mod.id}
                        onClick={() => setActiveTab(mod.id)}
                        style={{ 
                            backgroundColor: '#fff', 
                            borderRadius: '16px', 
                            padding: '24px', 
                            border: '1px solid #e2e8f0', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            height: '100%', 
                            cursor: 'pointer', 
                            position: 'relative', 
                            overflow: 'hidden',
                            transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseOver={(e) => { 
                            e.currentTarget.style.transform = 'translateY(-4px)'; 
                            e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)'; 
                            e.currentTarget.querySelector('.barra-lateral').style.width = '6px';
                        }}
                        onMouseOut={(e) => { 
                            e.currentTarget.style.transform = 'translateY(0)'; 
                            e.currentTarget.style.boxShadow = 'none'; 
                            e.currentTarget.querySelector('.barra-lateral').style.width = '4px';
                        }}
                    >
                        {/* Barra lateral colorida */}
                        <div className="barra-lateral" style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color, transition: 'width 0.2s' }}></div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
                            <div>
                                <p style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: mod.color, marginBottom: '8px', margin: 0 }}>
                                    {mod.stat}
                                </p>
                                <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                                    {mod.label}
                                </h3>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                                <mod.icon size={28} />
                            </div>
                        </div>
                        
                        <div style={{ marginTop: 'auto' }}>
                            <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.6', margin: '0 0 16px 0' }}>
                                {mod.desc}
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                                <span>Acessar Módulo</span> <ArrowRight size={16} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderContent = () => {
        if (activeTab === 'dashboard') return <OverviewWidgets />;
        
        const ActiveModule = () => {
            switch(activeTab) {
                case 'usuarios': return <AdminUsuarios />;
                case 'acessos': return <AdminAcessos />;
                case 'dados': return <AdminDados />;
                case 'logs': return <AdminLogs />;
                case 'seguranca': return <AdminSeguranca />;
                case 'bot-telegram': return <AdminTelegram />;
                case 'automacoes': return <AdminAutomacoes />;
                case 'avisos': return <AdminAvisos />;
                default: return null;
            }
        };
        
        return (
            <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', minHeight: '500px' }}>
                <ActiveModule />
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />

            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
                
                {/* Navbar Superior */}
                <div style={{ height: '80px', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Painel Administrativo</h2>
                </div>

                <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                    
                    {/* Cabeçalho da Página */}
                    <div style={{ marginBottom: '32px' }}>
                        <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '8px', margin: 0 }}>
                            Gestão Centralizada
                        </h1>
                        <p style={{ color: '#64748b', fontSize: '16px', margin: 0 }}>
                            Controle total sobre o portal do Grupo Correia.
                        </p>
                    </div>

                    {/* TABS DE NAVEGAÇÃO */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '32px', backgroundColor: '#fff', padding: '8px', borderRadius: '12px', border: '1px solid #e2e8f0', width: 'fit-content' }}>
                        
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: activeTab === 'dashboard' ? '#0f172a' : 'transparent', color: activeTab === 'dashboard' ? '#fff' : '#64748b' }}
                        >
                            <LayoutDashboard size={18} /> Visão Geral
                        </button>

                        <div style={{ width: '1px', backgroundColor: '#e2e8f0', margin: '0 5px' }}></div>

                        {modules.map((mod) => (
                            <button
                                key={mod.id}
                                onClick={() => setActiveTab(mod.id)}
                                style={{ 
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', 
                                    border: `2px solid ${activeTab === mod.id ? mod.color : 'transparent'}`, 
                                    backgroundColor: activeTab === mod.id ? '#fff' : 'transparent', 
                                    color: activeTab === mod.id ? mod.color : '#64748b' 
                                }}
                            >
                                <mod.icon size={18} /> {mod.label}
                            </button>
                        ))}
                    </div>

                    {/* CONTEÚDO ATIVO */}
                    {renderContent()}

                </div>
            </div>
        </div>
    );
};

export default AdminPanel;